import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, Statistic, Table, Tag, Tabs, Spin, Button, Progress, Tooltip } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartLine, faUsers, faLock, faUnlock, faRotate, faClock,
  faTriangleExclamation, faArrowTrendUp, faCheckCircle, faBullseye,
  faFire, faUserCheck, faUserXmark, faChartPie,
} from '@fortawesome/free-solid-svg-icons';
import ReactECharts from 'echarts-for-react';
import axios from 'axios';
import { SERVER_URL } from '../../../databucket';
import dayjs from 'dayjs';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#f97316', '#84cc16', '#6366f1', '#14b8a6', '#e11d48', '#a855f7'];

const STATUS_COLORS = {
  'New': '#3b82f6', 'Open': '#06b6d4', 'Active': '#10b981', 'In Progress': '#f59e0b',
  'On Hold': '#f97316', 'Closed': '#6b7280', 'Reopened': '#8b5cf6', 'Updated Data': '#14b8a6',
  'Duplicate': '#ef4444', 'Successful': '#22c55e', 'Rework': '#e11d48', 'Needs Data Rework': '#dc2626',
};

const CHANNEL_COLORS = {
  'Cold Calling': '#3b82f6', 'Email': '#10b981', 'LinkedIn': '#0a66c2', 'Linkedln': '#0a66c2', 'Reference': '#f59e0b',
};

const LeadAnalytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await axios.get(`${SERVER_URL}/api/lead-analytics`);
      setData(res);
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Lead Analytics fetch error:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const o = data?.overview || {};

  // ── Status donut chart ──
  const statusChartOption = useMemo(() => {
    if (!data?.byStatus) return {};
    return {
      tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
      legend: { type: 'scroll', bottom: 0, itemWidth: 10, itemHeight: 10, textStyle: { fontSize: 11 } },
      series: [{
        type: 'pie', radius: ['45%', '72%'], center: ['50%', '45%'],
        itemStyle: { borderRadius: 6, borderColor: '#fff', borderWidth: 2 },
        label: { show: false },
        emphasis: { label: { show: true, fontSize: 13, fontWeight: 700 } },
        data: data.byStatus.map((s, i) => ({
          name: s.status, value: s.count,
          itemStyle: { color: STATUS_COLORS[s.status] || COLORS[i % COLORS.length] },
        })),
      }],
    };
  }, [data?.byStatus]);

  // ── Channel donut chart ──
  const channelChartOption = useMemo(() => {
    if (!data?.byChannel) return {};
    return {
      tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
      legend: { type: 'scroll', bottom: 0, itemWidth: 10, itemHeight: 10, textStyle: { fontSize: 11 } },
      series: [{
        type: 'pie', radius: ['45%', '72%'], center: ['50%', '45%'],
        itemStyle: { borderRadius: 6, borderColor: '#fff', borderWidth: 2 },
        label: { show: false },
        emphasis: { label: { show: true, fontSize: 13, fontWeight: 700 } },
        data: data.byChannel.filter(c => c.channel !== '(none)').map((c, i) => ({
          name: c.channel, value: c.count,
          itemStyle: { color: CHANNEL_COLORS[c.channel] || COLORS[i % COLORS.length] },
        })),
      }],
    };
  }, [data?.byChannel]);

  // ── Per-user bar chart (top 10) ──
  const userBarOption = useMemo(() => {
    if (!data?.perUser) return {};
    const top = data.perUser.slice(0, 12);
    return {
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
      grid: { left: 140, right: 30, top: 20, bottom: 30 },
      xAxis: { type: 'value' },
      yAxis: {
        type: 'category', inverse: true,
        data: top.map(u => u.user.length > 18 ? u.user.slice(0, 16) + '...' : u.user),
        axisLabel: { fontSize: 11 },
      },
      series: [
        { name: 'Assigned', type: 'bar', stack: 'total', data: top.map(u => u.assigned - u.locked), itemStyle: { color: '#3b82f6', borderRadius: [0, 0, 0, 0] } },
        { name: 'Locked', type: 'bar', stack: 'total', data: top.map(u => u.locked), itemStyle: { color: '#ef4444', borderRadius: [0, 4, 4, 0] } },
      ],
    };
  }, [data?.perUser]);

  // ── All statuses for matrix columns ──
  const allStatuses = useMemo(() => {
    if (!data?.byStatus) return [];
    return data.byStatus.map(s => s.status).filter(Boolean);
  }, [data?.byStatus]);

  // ── Per-user table columns ──
  const userColumns = [
    {
      title: 'User', dataIndex: 'user', key: 'user', fixed: 'left', width: 170,
      render: (t) => <span style={{ fontWeight: 600 }}>{t}</span>,
      sorter: (a, b) => a.user.localeCompare(b.user),
    },
    {
      title: 'Assigned', dataIndex: 'assigned', key: 'assigned', width: 90, align: 'center',
      render: (v) => <span style={{ fontWeight: 700, color: '#3b82f6' }}>{v?.toLocaleString()}</span>,
      sorter: (a, b) => a.assigned - b.assigned, defaultSortOrder: 'descend',
    },
    {
      title: 'Locked', dataIndex: 'locked', key: 'locked', width: 80, align: 'center',
      render: (v) => v > 0 ? <Tag color="red" style={{ borderRadius: 10, fontWeight: 700 }}>{v}</Tag> : <span style={{ color: '#cbd5e1' }}>0</span>,
      sorter: (a, b) => a.locked - b.locked,
    },
    {
      title: 'Pipeline', dataIndex: 'inPipeline', key: 'inPipeline', width: 80, align: 'center',
      render: (v) => v > 0 ? <Tag color="green" style={{ borderRadius: 10, fontWeight: 700 }}>{v}</Tag> : <span style={{ color: '#cbd5e1' }}>0</span>,
      sorter: (a, b) => a.inPipeline - b.inPipeline,
    },
    {
      title: 'Today', dataIndex: 'touchedToday', key: 'touchedToday', width: 75, align: 'center',
      render: (v) => v > 0 ? <span style={{ fontWeight: 700, color: '#10b981' }}>{v}</span> : <span style={{ color: '#cbd5e1' }}>0</span>,
      sorter: (a, b) => a.touchedToday - b.touchedToday,
    },
    {
      title: 'This Week', dataIndex: 'touchedThisWeek', key: 'touchedThisWeek', width: 90, align: 'center',
      render: (v) => v > 0 ? <span style={{ fontWeight: 600, color: '#3b82f6' }}>{v}</span> : <span style={{ color: '#cbd5e1' }}>0</span>,
      sorter: (a, b) => a.touchedThisWeek - b.touchedThisWeek,
    },
    {
      title: 'Overdue Follow-ups', dataIndex: 'followupOverdue', key: 'followupOverdue', width: 130, align: 'center',
      render: (v) => v > 0 ? <Tag color="orange" style={{ borderRadius: 10, fontWeight: 700 }}>{v}</Tag> : <span style={{ color: '#cbd5e1' }}>0</span>,
      sorter: (a, b) => a.followupOverdue - b.followupOverdue,
    },
    {
      title: 'Will Unassign', dataIndex: 'willUnassign', key: 'willUnassign', width: 110, align: 'center',
      render: (v) => v > 0 ? <Tag color="volcano" style={{ borderRadius: 10, fontWeight: 700 }}>{v}</Tag> : <span style={{ color: '#cbd5e1' }}>0</span>,
      sorter: (a, b) => a.willUnassign - b.willUnassign,
    },
    {
      title: 'New Today', dataIndex: 'createdToday', key: 'createdToday', width: 90, align: 'center',
      render: (v) => v > 0 ? <span style={{ fontWeight: 700, color: '#8b5cf6' }}>{v}</span> : <span style={{ color: '#cbd5e1' }}>0</span>,
      sorter: (a, b) => a.createdToday - b.createdToday,
    },
  ];

  // ── Status matrix columns ──
  const statusMatrixColumns = [
    {
      title: 'User', dataIndex: 'user', key: 'user', fixed: 'left', width: 170,
      render: (t) => <span style={{ fontWeight: 600 }}>{t}</span>,
    },
    ...allStatuses.map((status) => ({
      title: status, dataIndex: status, key: status, width: 100, align: 'center',
      render: (v) => {
        if (!v) return <span style={{ color: '#e2e8f0' }}>-</span>;
        return <span style={{ fontWeight: 600, color: STATUS_COLORS[status] || '#1e293b' }}>{v}</span>;
      },
      sorter: (a, b) => (a[status] || 0) - (b[status] || 0),
    })),
    {
      title: 'Total', key: 'total', width: 80, align: 'center', fixed: 'right',
      render: (_, row) => {
        const total = allStatuses.reduce((s, st) => s + (row[st] || 0), 0);
        return <span style={{ fontWeight: 800 }}>{total.toLocaleString()}</span>;
      },
    },
  ];

  const statusMatrixData = useMemo(() => {
    if (!data?.perUser) return [];
    return data.perUser.map((u) => ({
      key: u.user,
      user: u.user,
      ...u.statuses,
    }));
  }, [data?.perUser]);

  // ── Auto-unassign risk table ──
  const riskColumns = [
    { title: 'User', dataIndex: '_id', key: 'user', width: 170, render: (t) => <span style={{ fontWeight: 600 }}>{t}</span> },
    {
      title: 'At Risk', dataIndex: 'count', key: 'count', width: 90, align: 'center',
      render: (v) => <Tag color="volcano" style={{ borderRadius: 10, fontWeight: 700, fontSize: 13 }}>{v}</Tag>,
    },
    {
      title: 'Sample Leads', dataIndex: 'leads', key: 'leads',
      render: (leads) => (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {(leads || []).slice(0, 5).map((l, i) => (
            <Tooltip key={i} title={`${l.agencyName || 'No name'} — ${Math.floor(l.daysAssigned)} days assigned`}>
              <Tag style={{ borderRadius: 8, fontSize: 11 }}>
                #{l.leadID} · {Math.floor(l.daysAssigned)}d
              </Tag>
            </Tooltip>
          ))}
          {leads?.length > 5 && <Tag style={{ borderRadius: 8, fontSize: 11, color: '#64748b' }}>+{leads.length - 5} more</Tag>}
        </div>
      ),
    },
  ];

  // ── Status distribution bar for overview ──
  const statusBarOption = useMemo(() => {
    if (!data?.byStatus) return {};
    const sorted = [...data.byStatus].sort((a, b) => b.count - a.count);
    return {
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
      grid: { left: 120, right: 40, top: 10, bottom: 30 },
      xAxis: { type: 'value', axisLabel: { fontSize: 11 } },
      yAxis: { type: 'category', inverse: true, data: sorted.map(s => s.status), axisLabel: { fontSize: 11 } },
      series: [{
        type: 'bar', barWidth: 18,
        data: sorted.map((s, i) => ({
          value: s.count,
          itemStyle: { color: STATUS_COLORS[s.status] || COLORS[i % COLORS.length], borderRadius: [0, 4, 4, 0] },
        })),
        label: { show: true, position: 'right', fontSize: 11, fontWeight: 600 },
      }],
    };
  }, [data?.byStatus]);

  if (loading && !data) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <Spin size="large" tip="Loading lead analytics..." />
      </div>
    );
  }

  return (
    <div style={{ padding: '20px 24px', background: '#f8fafc', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontWeight: 800, fontSize: 22, color: '#0f172a' }}>
          <FontAwesomeIcon icon={faChartLine} style={{ marginRight: 10, color: '#3b82f6' }} />
          Lead Analytics
        </h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {lastRefresh && (
            <span style={{ fontSize: 12, color: '#94a3b8' }}>
              Updated {dayjs(lastRefresh).format('hh:mm A')}
            </span>
          )}
          <Button icon={<FontAwesomeIcon icon={faRotate} />} onClick={fetchData} loading={loading}>
            Refresh
          </Button>
        </div>
      </div>

      {/* ── Overview Cards ── */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        {[
          { title: 'Total Leads', value: o.total, color: '#1e293b', border: '#e2e8f0', icon: faChartPie },
          { title: 'Assigned', value: o.assigned, color: '#3b82f6', border: '#93c5fd', icon: faUserCheck },
          { title: 'Unassigned', value: o.unassigned, color: '#f59e0b', border: '#fcd34d', icon: faUserXmark },
          { title: 'Locked', value: o.locked, color: '#ef4444', border: '#fca5a5', icon: faLock },
          { title: 'In Pipeline', value: o.inPipeline, color: '#8b5cf6', border: '#c4b5fd', icon: faBullseye },
          { title: 'Successful', value: o.successful, color: '#10b981', border: '#6ee7b7', icon: faCheckCircle },
          { title: 'Follow-up Overdue', value: o.followupOverdue, color: '#f97316', border: '#fdba74', icon: faClock },
          { title: 'Will Unassign', value: o.willUnassign, color: '#dc2626', border: '#f87171', icon: faUnlock },
          { title: 'At Risk (25-30d)', value: o.atRiskUnassign, color: '#d97706', border: '#fcd34d', icon: faTriangleExclamation },
          { title: 'New This Week', value: o.createdThisWeek, color: '#6366f1', border: '#a5b4fc', icon: faArrowTrendUp },
        ].map((c, i) => (
          <Card key={i} size="small" style={{ borderRadius: 12, border: `1.5px solid ${c.border}`, flex: '1 1 0', minWidth: 140 }}>
            <Statistic
              title={<span style={{ fontSize: 12, color: '#64748b' }}>{c.title}</span>}
              value={c.value ?? 0}
              valueStyle={{ color: c.color, fontWeight: 800, fontSize: 22 }}
              prefix={<FontAwesomeIcon icon={c.icon} style={{ fontSize: 14, marginRight: 4 }} />}
            />
          </Card>
        ))}
      </div>

      {/* ── Charts Row ── */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
        <Card title={<span style={{ fontWeight: 700 }}><FontAwesomeIcon icon={faChartPie} style={{ marginRight: 6, color: '#3b82f6' }} />Status Distribution</span>} size="small" style={{ flex: '1 1 320px', borderRadius: 12 }}>
          <ReactECharts option={statusBarOption} style={{ height: 320 }} />
        </Card>
        <Card title={<span style={{ fontWeight: 700 }}><FontAwesomeIcon icon={faChartPie} style={{ marginRight: 6, color: '#10b981' }} />By Channel</span>} size="small" style={{ flex: '0 1 320px', borderRadius: 12 }}>
          <ReactECharts option={channelChartOption} style={{ height: 320 }} />
        </Card>
        <Card title={<span style={{ fontWeight: 700 }}><FontAwesomeIcon icon={faUsers} style={{ marginRight: 6, color: '#8b5cf6' }} />Leads per User</span>} size="small" style={{ flex: '1 1 400px', borderRadius: 12 }}>
          <ReactECharts option={userBarOption} style={{ height: 320 }} />
        </Card>
      </div>

      {/* ── Tabs: Tables ── */}
      <Card style={{ borderRadius: 12 }}>
        <Tabs defaultActiveKey="1" items={[
          {
            key: '1',
            label: <span><FontAwesomeIcon icon={faUsers} style={{ marginRight: 6 }} />User Performance</span>,
            children: (
              <Table
                columns={userColumns}
                dataSource={data?.perUser || []}
                rowKey="user"
                size="small"
                scroll={{ x: 1200 }}
                pagination={false}
                summary={(rows) => {
                  const totals = rows.reduce((acc, r) => ({
                    assigned: acc.assigned + (r.assigned || 0),
                    locked: acc.locked + (r.locked || 0),
                    inPipeline: acc.inPipeline + (r.inPipeline || 0),
                    touchedToday: acc.touchedToday + (r.touchedToday || 0),
                    touchedThisWeek: acc.touchedThisWeek + (r.touchedThisWeek || 0),
                    followupOverdue: acc.followupOverdue + (r.followupOverdue || 0),
                    willUnassign: acc.willUnassign + (r.willUnassign || 0),
                    createdToday: acc.createdToday + (r.createdToday || 0),
                  }), { assigned: 0, locked: 0, inPipeline: 0, touchedToday: 0, touchedThisWeek: 0, followupOverdue: 0, willUnassign: 0, createdToday: 0 });
                  return (
                    <Table.Summary.Row style={{ background: '#f1f5f9', fontWeight: 800 }}>
                      <Table.Summary.Cell index={0}>TOTAL</Table.Summary.Cell>
                      <Table.Summary.Cell index={1} align="center">{totals.assigned.toLocaleString()}</Table.Summary.Cell>
                      <Table.Summary.Cell index={2} align="center">{totals.locked}</Table.Summary.Cell>
                      <Table.Summary.Cell index={3} align="center">{totals.inPipeline}</Table.Summary.Cell>
                      <Table.Summary.Cell index={4} align="center">{totals.touchedToday}</Table.Summary.Cell>
                      <Table.Summary.Cell index={5} align="center">{totals.touchedThisWeek}</Table.Summary.Cell>
                      <Table.Summary.Cell index={6} align="center">{totals.followupOverdue}</Table.Summary.Cell>
                      <Table.Summary.Cell index={7} align="center">{totals.willUnassign}</Table.Summary.Cell>
                      <Table.Summary.Cell index={8} align="center">{totals.createdToday}</Table.Summary.Cell>
                    </Table.Summary.Row>
                  );
                }}
              />
            ),
          },
          {
            key: '2',
            label: <span><FontAwesomeIcon icon={faChartPie} style={{ marginRight: 6 }} />Status Matrix</span>,
            children: (
              <Table
                columns={statusMatrixColumns}
                dataSource={statusMatrixData}
                rowKey="user"
                size="small"
                scroll={{ x: allStatuses.length * 100 + 250 }}
                pagination={false}
              />
            ),
          },
          {
            key: '3',
            label: <span><FontAwesomeIcon icon={faTriangleExclamation} style={{ marginRight: 6, color: '#ef4444' }} />Auto-Unassign Risk</span>,
            children: (
              <>
                {(data?.autoUnassignRisk || []).length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>
                    <FontAwesomeIcon icon={faCheckCircle} style={{ fontSize: 32, marginBottom: 8, color: '#10b981' }} />
                    <div style={{ fontSize: 14, fontWeight: 600 }}>No leads at risk of auto-unassign</div>
                    <div style={{ fontSize: 12 }}>All assigned leads are within the 25-day threshold</div>
                  </div>
                ) : (
                  <Table
                    columns={riskColumns}
                    dataSource={data?.autoUnassignRisk || []}
                    rowKey="_id"
                    size="small"
                    pagination={false}
                  />
                )}
              </>
            ),
          },
          {
            key: '4',
            label: <span><FontAwesomeIcon icon={faFire} style={{ marginRight: 6, color: '#f59e0b' }} />Status Breakdown</span>,
            children: (
              <Table
                columns={[
                  { title: 'Status', dataIndex: 'status', key: 'status', width: 180,
                    render: (t) => <Tag color={STATUS_COLORS[t] || '#6b7280'} style={{ borderRadius: 10, fontWeight: 700 }}>{t}</Tag>,
                  },
                  { title: 'Count', dataIndex: 'count', key: 'count', width: 100, align: 'center',
                    render: (v) => <span style={{ fontWeight: 700 }}>{v?.toLocaleString()}</span>,
                    sorter: (a, b) => a.count - b.count, defaultSortOrder: 'descend',
                  },
                  { title: '% of Total', key: 'pct', width: 200,
                    render: (_, row) => {
                      const pct = o.total ? ((row.count / o.total) * 100).toFixed(1) : 0;
                      return <Progress percent={Number(pct)} size="small" strokeColor={STATUS_COLORS[row.status] || '#3b82f6'} />;
                    },
                  },
                ]}
                dataSource={data?.byStatus || []}
                rowKey="status"
                size="small"
                pagination={false}
              />
            ),
          },
          {
            key: '5',
            label: <span><FontAwesomeIcon icon={faBullseye} style={{ marginRight: 6, color: '#06b6d4' }} />Channel Breakdown</span>,
            children: (
              <Table
                columns={[
                  { title: 'Channel', dataIndex: 'channel', key: 'channel', width: 180,
                    render: (t) => <Tag color={CHANNEL_COLORS[t] || '#6b7280'} style={{ borderRadius: 10, fontWeight: 700 }}>{t || '(none)'}</Tag>,
                  },
                  { title: 'Count', dataIndex: 'count', key: 'count', width: 100, align: 'center',
                    render: (v) => <span style={{ fontWeight: 700 }}>{v?.toLocaleString()}</span>,
                    sorter: (a, b) => a.count - b.count, defaultSortOrder: 'descend',
                  },
                  { title: '% of Total', key: 'pct', width: 200,
                    render: (_, row) => {
                      const pct = o.total ? ((row.count / o.total) * 100).toFixed(1) : 0;
                      return <Progress percent={Number(pct)} size="small" strokeColor={CHANNEL_COLORS[row.channel] || '#3b82f6'} />;
                    },
                  },
                ]}
                dataSource={data?.byChannel || []}
                rowKey="channel"
                size="small"
                pagination={false}
              />
            ),
          },
        ]} />
      </Card>
    </div>
  );
};

export default LeadAnalytics;
